import { useState } from "react";
import type {
  LaunchableParameter,
  LaunchableParameterBinding,
  LaunchableManagedSecretReference,
  NGCAPIKeySpec,
} from "server";
import type { NgcApiKeyDeployResult } from "../configuration/ngc-api-key/NgcApiKeyOption";
import { useNgcApiKeyDeployOption } from "./useNgcApiKeyDeployOption";
import { getLaunchableParameterBindings } from "../configuration/parameters/LaunchableParameters";

export type DeploymentConfirmationStep =
  | "compute"
  | "parameters"
  | "ngc-api-key";

interface UseDeployRequestOptions {
  isPublic?: boolean;
  loading: boolean;
  initLoading: boolean;
  computeHasErrors: boolean;
  ngcApiKeySpec?: NGCAPIKeySpec | null;
  parameters: LaunchableParameter[];
  parameterValues: Record<string, string>;
  parameterSecretSelections?: Record<string, LaunchableManagedSecretReference>;
  selectedLocation: string | null;
  missingRequiredParameters: boolean;
  onDeploy: (
    ngcApiKeySpec?: NGCAPIKeySpec | null,
    parameterBindings?: LaunchableParameterBinding[],
    location?: string | null,
  ) => Promise<NgcApiKeyDeployResult | void> | NgcApiKeyDeployResult | void;
}

export function useDeployRequest({
  isPublic,
  loading,
  initLoading,
  computeHasErrors,
  ngcApiKeySpec,
  parameters,
  parameterValues,
  parameterSecretSelections = {},
  selectedLocation,
  missingRequiredParameters,
  onDeploy,
}: UseDeployRequestOptions) {
  const [confirmationSteps, setConfirmationSteps] = useState<
    DeploymentConfirmationStep[]
  >([]);
  const [confirmationStepIndex, setConfirmationStepIndex] = useState(0);
  const ngcApiKey = useNgcApiKeyDeployOption({
    isPublic,
    loading,
    initLoading,
    ngcApiKeySpec,
    onDeploy: (selectedNgcApiKeySpec) =>
      onDeploy(
        selectedNgcApiKeySpec,
        getLaunchableParameterBindings(
          parameters,
          parameterValues,
          parameterSecretSelections,
        ),
        selectedLocation,
      ),
  });
  const parametersHaveErrors = !isPublic && missingRequiredParameters;
  const shouldWarnAboutMissingNgcApiKey =
    ngcApiKey.shouldShowSection && !ngcApiKey.checked;

  const handleDeployRequest = () => {
    const nextConfirmationSteps: DeploymentConfirmationStep[] = [];
    if (computeHasErrors) {
      nextConfirmationSteps.push("compute");
    }
    if (parametersHaveErrors) {
      nextConfirmationSteps.push("parameters");
    }
    if (shouldWarnAboutMissingNgcApiKey) {
      nextConfirmationSteps.push("ngc-api-key");
    }

    if (nextConfirmationSteps.length > 0) {
      setConfirmationSteps(nextConfirmationSteps);
      setConfirmationStepIndex(0);
      ngcApiKey.onDeployWithoutNgcApiKeyConfirmationOpenChange(true);
      return;
    }

    return ngcApiKey.onDeploy();
  };

  const handleConfirmDeploy = async () => {
    if (parametersHaveErrors || computeHasErrors) return;
    if (shouldWarnAboutMissingNgcApiKey) {
      await ngcApiKey.onConfirmDeployWithoutNgcApiKey();
      return;
    }
    ngcApiKey.onDeployWithoutNgcApiKeyConfirmationOpenChange(false);
    await ngcApiKey.onDeploy();
  };

  const handleIncludeNgcApiKeyAndDeploy = async () => {
    if (parametersHaveErrors || computeHasErrors) return;
    await ngcApiKey.onIncludeNgcApiKeyAndDeploy();
  };

  const handleConfirmationOpenChange = (open: boolean) => {
    if (!open) {
      setConfirmationSteps([]);
      setConfirmationStepIndex(0);
    }
    ngcApiKey.onDeployWithoutNgcApiKeyConfirmationOpenChange(open);
  };

  const handleNextConfirmationStep = () => {
    setConfirmationStepIndex((current) =>
      Math.min(current + 1, confirmationSteps.length - 1),
    );
  };

  return {
    ngcApiKey,
    confirmationSteps,
    confirmationStepIndex,
    computeHasErrors,
    parametersHaveErrors,
    shouldWarnAboutMissingNgcApiKey,
    handleDeployRequest,
    handleConfirmDeploy,
    handleIncludeNgcApiKeyAndDeploy,
    handleConfirmationOpenChange,
    handleNextConfirmationStep,
  };
}
